import { useEffect, useRef } from 'react';
import { usePrefersReducedMotion } from '../../hooks/usePrefersReducedMotion';
import { DIGITAL_BACKDROP_BRAND } from '../../constants/digitalBackdropBrand';

const CELL_SIZE = 14;

function lerp(a, b, t) {
  return a + (b - a) * t;
}

function buildBrandMask(cols, rows, width, height, brand) {
  const mask = new Uint8Array(cols * rows);
  if (width <= 0 || height <= 0 || cols <= 0 || rows <= 0) return mask;

  const sample = document.createElement('canvas');
  sample.width = width;
  sample.height = height;
  const ctx = sample.getContext('2d');
  if (!ctx) return mask;

  ctx.fillStyle = '#000';
  ctx.fillRect(0, 0, width, height);
  ctx.fillStyle = '#fff';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  const headlineSize = Math.min(width * 0.13, 132);
  const taglineSize = Math.min(width * 0.028, 26);
  const hintSize = Math.min(width * 0.02, 17);
  const centerY = height * 0.56;

  ctx.font = `700 ${headlineSize}px system-ui, -apple-system, sans-serif`;
  ctx.fillText(brand.headline, width / 2, centerY);

  ctx.font = `500 ${taglineSize}px system-ui, -apple-system, sans-serif`;
  ctx.letterSpacing = '0.12em';
  ctx.fillText(brand.tagline, width / 2, centerY + headlineSize * 0.72);

  ctx.font = `400 ${hintSize}px system-ui, -apple-system, sans-serif`;
  ctx.letterSpacing = '0.18em';
  ctx.fillText(brand.hint, width / 2, centerY + headlineSize * 0.72 + taglineSize * 1.35);

  const { data } = ctx.getImageData(0, 0, width, height);

  for (let row = 0; row < rows; row += 1) {
    for (let col = 0; col < cols; col += 1) {
      const cx = Math.min(width - 1, Math.floor(col * CELL_SIZE + CELL_SIZE / 2));
      const cy = Math.min(height - 1, Math.floor(row * CELL_SIZE + CELL_SIZE / 2));
      const pixel = (cy * width + cx) * 4;
      if (data[pixel + 3] > 48) {
        mask[row * cols + col] = 1;
      }
    }
  }

  return mask;
}

function DigitalPixelBackdrop({ className = '' }) {
  const canvasRef = useRef(null);
  const cellsRef = useRef(null);
  const maskRef = useRef(null);
  const gridRef = useRef({ cols: 0, rows: 0 });
  const reducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    let rafId = 0;
    let running = true;
    let frame = 0;

    const initCells = (cols, rows, width, height) => {
      const cells = new Float32Array(cols * rows);
      const mask = buildBrandMask(cols, rows, width, height, DIGITAL_BACKDROP_BRAND);

      for (let i = 0; i < cells.length; i += 1) {
        if (mask[i]) {
          cells[i] = 0.72 + Math.random() * 0.22;
        } else {
          cells[i] = 0.2 + Math.random() * 0.55;
        }
      }

      cellsRef.current = cells;
      maskRef.current = mask;
      gridRef.current = { cols, rows };
    };

    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;

      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const width = parent.clientWidth;
      const height = parent.clientHeight;

      if (width <= 0 || height <= 0) return;

      canvas.width = Math.max(1, Math.floor(width * dpr));
      canvas.height = Math.max(1, Math.floor(height * dpr));
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const cols = Math.ceil(width / CELL_SIZE);
      const rows = Math.ceil(height / CELL_SIZE);
      initCells(cols, rows, width, height);

      if (reducedMotion) {
        draw();
      }
    };

    const draw = () => {
      if (!running) return;

      const width = canvas.clientWidth;
      const height = canvas.clientHeight;
      const { cols, rows } = gridRef.current;
      const cells = cellsRef.current;
      const mask = maskRef.current;

      if (!cells || !mask || cols === 0 || rows === 0) {
        rafId = requestAnimationFrame(draw);
        return;
      }

      frame += 1;

      if (!reducedMotion) {
        const updates = Math.max(8, Math.floor(cols * rows * 0.014));
        for (let i = 0; i < updates; i += 1) {
          const idx = Math.floor(Math.random() * cells.length);
          if (mask[idx]) {
            const wave = 0.68 + 0.26 * Math.sin(frame * 0.04 + idx * 0.15);
            cells[idx] = wave;
          } else {
            cells[idx] = 0.12 + Math.random() * 0.5;
          }
        }
      }

      ctx.fillStyle = '#030308';
      ctx.fillRect(0, 0, width, height);

      for (let row = 0; row < rows; row += 1) {
        const t = row / Math.max(rows - 1, 1);
        const baseR = lerp(108, 14, t);
        const baseG = lerp(32, 158, t);
        const baseB = lerp(168, 198, t);

        for (let col = 0; col < cols; col += 1) {
          const idx = row * cols + col;
          const isBrand = mask[idx] === 1;
          const pulse = cells[idx];

          let r = baseR;
          let g = baseG;
          let b = baseB;
          let alpha = 0.08 + pulse * 0.5;

          if (isBrand) {
            r = lerp(baseR, 224, 0.5);
            g = lerp(baseG, 101, 0.5);
            b = lerp(baseB, 9, 0.3);
            alpha = 0.22 + pulse * 0.48;
          }

          ctx.fillStyle = `rgba(${r | 0},${g | 0},${b | 0},${alpha})`;
          ctx.fillRect(col * CELL_SIZE, row * CELL_SIZE, CELL_SIZE - 1, CELL_SIZE - 1);
        }
      }

      if (!reducedMotion) {
        rafId = requestAnimationFrame(draw);
      }
    };

    resize();
    draw();

    const observer = new ResizeObserver(resize);
    if (canvas.parentElement) {
      observer.observe(canvas.parentElement);
    }

    return () => {
      running = false;
      cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, [reducedMotion]);

  return (
    <canvas
      ref={canvasRef}
      className={`hero-backdrop__digital-canvas ${className}`.trim()}
      aria-hidden="true"
    />
  );
}

export default DigitalPixelBackdrop;
